import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import {
  EntitySelectorBaseProps,
  useEntitySearch,
  EntityDropdownContent,
} from "@/components/EntitySelectorBase";

interface MultiEntitySelectorProps<T extends { id: string }>
  extends EntitySelectorBaseProps<T> {
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
}

export function MultiEntitySelector<T extends { id: string }>({
  entities,
  listLoading,
  fetchEntities,
  getDisplayName,
  getSecondaryInfo,
  renderExtraInfo,
  selectedIds,
  onSelectedIdsChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  className,
  disabled = false,
  variant = "default",
  size = "default",
  excludeId,
  entityLabel = "items",
  onCreateNew,
  createNewLabel,
  resultLimit,
  renderRowMeta,
  renderRowPrice,
  createRows,
  onLoadMore,
  hasMore,
  isLoadingMore,
  popoverMinWidth,
  pinnedEntityIds,
  pinnedGroupLabel,
}: MultiEntitySelectorProps<T>) {
  const {
    open,
    setOpen,
    searchValue,
    setSearchValue,
    isSearching,
    displayedEntityIds,
    hasLoadedResults,
    loadMore,
    closeSelector,
  } = useEntitySearch({ fetchEntities, listLoading, onLoadMore });

  const handleSelect = useCallback(
    (id: string) => {
      const newIds = selectedIds.includes(id)
        ? selectedIds.filter((existingId) => existingId !== id)
        : [...selectedIds, id];
      onSelectedIdsChange(newIds);
    },
    [selectedIds, onSelectedIdsChange],
  );

  const handleRemove = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      onSelectedIdsChange(
        selectedIds.filter((existingId) => existingId !== id),
      );
    },
    [selectedIds, onSelectedIdsChange],
  );

  const isGhost = variant === "ghost";
  const hasSelection = selectedIds.length > 0;

  const triggerContent = hasSelection ? (
    <div className="flex flex-wrap gap-1">
      {selectedIds.map((id) => {
        const entity = entities.get(id);
        if (!entity) {
          return null;
        }
        return (
          <Badge key={id} variant="secondary" className="text-xs px-1.5 py-0">
            {getDisplayName(entity)}
            <button
              type="button"
              className="ml-1 rounded-full outline-none hover:bg-muted-foreground/20"
              onClick={(e) => handleRemove(e, id)}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        );
      })}
    </div>
  ) : (
    <span>{placeholder}</span>
  );

  return (
    <div className={cn("w-full", className)}>
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          {isGhost ? (
            <button
              type="button"
              disabled={disabled}
              className={cn(
                "flex h-8 w-full items-center justify-between rounded-md bg-transparent px-2 py-1 text-sm text-left transition-colors",
                "border border-transparent",
                "hover:border-input hover:bg-background",
                "focus:border-ring focus:bg-background focus:outline-none focus:ring-1 focus:ring-ring",
                "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-transparent disabled:hover:bg-transparent",
                !hasSelection && "text-muted-foreground",
              )}
            >
              {triggerContent}
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </button>
          ) : (
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className={cn(
                "w-full justify-between text-left font-normal",
                size === "sm" && "h-8 text-sm",
                "h-auto min-h-9 py-1.5",
              )}
            >
              {triggerContent}
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent
          className="p-0"
          align="start"
          style={{
            width: "var(--radix-popover-trigger-width)",
            minWidth: popoverMinWidth,
          }}
        >
          <EntityDropdownContent
            entities={entities}
            displayedEntityIds={displayedEntityIds}
            excludeId={excludeId}
            isSearching={isSearching}
            hasLoadedResults={hasLoadedResults}
            isItemSelected={(id) => selectedIds.includes(id)}
            onSelectItem={handleSelect}
            getDisplayName={getDisplayName}
            getSecondaryInfo={getSecondaryInfo}
            renderExtraInfo={renderExtraInfo}
            searchValue={searchValue}
            searchPlaceholder={searchPlaceholder}
            setSearchValue={setSearchValue}
            entityLabel={entityLabel}
            onCreateNew={onCreateNew}
            createNewLabel={createNewLabel}
            resultLimit={resultLimit}
            renderRowMeta={renderRowMeta}
            renderRowPrice={renderRowPrice}
            createRows={createRows}
            closeSelector={closeSelector}
            onLoadMore={onLoadMore ? loadMore : undefined}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            pinnedEntityIds={pinnedEntityIds}
            pinnedGroupLabel={pinnedGroupLabel}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
